import React, { Fragment, useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Spinner,
    Collapse,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Label,
} from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import CardHeaderSpan from '../../../Utils/CommonComponents/CardHeaderSpan';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import { getLabel } from '../../../sharedFunctions/CommonLable';
import moment from 'moment';
import Loader from '../../../Layout/Loader';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux-toolkit/store';
import { setSelectedDate as setReduxSelectedDate } from '../../../redux-toolkit/positionSlice';

interface MeNCAContainerProps {
    onTabChange?: (tab: 'JW' | 'Doctor' | 'Pharmacy' | 'Stockist' | 'NCA') => void;
    onRefreshCounts?: () => void;
    agendaCounts?: {
        jw: number;
        doctorReported: number;
        doctorTotal: number;
        pharmacyReported: number;
        pharmacyTotal: number;
        stockistReported: number;
        stockistTotal: number;
        nca: number;
    };
}

export default function MeNCAContainer({ onTabChange, onRefreshCounts, agendaCounts }: MeNCAContainerProps = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const reduxSelectedDate = useSelector((state: RootState) => state.position.selectedDate);
    const initialDate = location.state?.date || reduxSelectedDate || localStorage.getItem("selectedDate") || moment().format('YYYY-MM-DD');
    const [selectedDate, setSelectedDate] = useState<string>(initialDate);

    const [ncaList, setNcaList] = useState<any[]>([]);
    const [loadingNcaData, setLoadingNcaData] = useState<boolean>(false);
    const [submittingNcaData, setSubmittingNcaData] = useState<boolean>(false);

    // Unplanned NCA Modal State
    const [unplannedModalOpen, setUnplannedModalOpen] = useState<boolean>(false);
    const [loadingAgendas, setLoadingAgendas] = useState<boolean>(false);
    const [loadingTowns, setLoadingTowns] = useState<boolean>(false);
    const [submittingUnplannedNca, setSubmittingUnplannedNca] = useState<boolean>(false);
    const [agendaTypeList, setAgendaTypeList] = useState<any[]>([]);
    const [townsList, setTownsList] = useState<any[]>([]);
    const [selectedAgendaId, setSelectedAgendaId] = useState<string>("");
    const [selectedTownId, setSelectedTownId] = useState<string>("");
    const [ncaRemark, setNcaRemark] = useState<string>("");

    // Track expanded card indices
    const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});

    const toggleCard = (index: number) => {
        setExpandedCards(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Fetch NCA Agenda Types via /api/plans/mtp/agendas?agenda_type=NCA&date=YYYY-MM-DD
    const fetchAgendasList = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingAgendas(true);
        try {
            const formattedDateParam = moment(selectedDate).format('YYYY-MM-DD');
            const url = `${serverUrl}${API.getMtpAgendas}`;
            const res = await getData(url, true, { agenda_type: 'NCA', date: formattedDateParam });
            console.log("NCA Agendas API response:", res);
            const payload = res?.data?.data || res?.data;
            const agendaArr = Array.isArray(payload?.AgendaTypes) ? payload.AgendaTypes : (Array.isArray(payload) ? payload : []);
            setAgendaTypeList(agendaArr);
            if (agendaArr.length > 0) {
                setSelectedAgendaId(String(agendaArr[0].Agendaid || agendaArr[0].id || ''));
            }
        } catch (err) {
            console.error("Error fetching NCA agendas:", err);
            setAgendaTypeList([]);
        } finally {
            setLoadingAgendas(false);
        }
    };

    // Helper to extract towns array from any API response shape
    const extractTownsArray = (resData: any): any[] => {
        if (!resData) return [];
        if (Array.isArray(resData)) return resData;

        const candidates = [
            resData?.data,
            resData?.d,
            resData?.towns,
            resData?.Towns,
            resData?.territories,
            resData?.items,
            resData?.result,
            resData?.data?.data,
            resData?.data?.towns,
            resData?.data?.Towns,
            resData?.data?.territories,
            resData?.data?.items,
            resData?.data?.result,
            resData?.d?.data,
            resData?.d?.towns
        ];

        for (const cand of candidates) {
            if (Array.isArray(cand) && cand.length > 0) {
                return cand;
            }
        }

        return [];
    };

    // Fetch Towns via /api/territories/towns
    const fetchTownsList = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingTowns(true);
        try {
            const url = `${serverUrl}${API.towns}`;
            
            // Direct request
            let res = await getData(url, true);
            console.log("Towns API response (/api/territories/towns):", res);
            let tList = extractTownsArray(res?.data ?? res);

            // Fallback request with position_id/employee_id/company_id if empty
            if (!tList || tList.length === 0) {
                const posId = localStorage.getItem("positionId") || localStorage.getItem("position_id");
                const empId = localStorage.getItem("employeeId") || localStorage.getItem("employee_id");
                const compId = localStorage.getItem("companyId") || localStorage.getItem("company_id");
                const params: any = {};
                if (posId) params.position_id = posId;
                if (empId) params.employee_id = empId;
                if (compId) params.company_id = compId;

                const resWithParams = await getData(url, true, params);
                console.log("Towns API response with params:", resWithParams);
                tList = extractTownsArray(resWithParams?.data ?? resWithParams);
            }

            setTownsList(tList);
            if (tList.length > 0) {
                const first = tList[0];
                const firstId = typeof first === 'object' && first !== null
                    ? (first.Itownid ?? first.itownid ?? first.town_id ?? first.TownId ?? first.id ?? first.value ?? first.Stownname ?? first.town_name ?? first.name ?? "")
                    : first;
                setSelectedTownId(String(firstId));
            }
        } catch (err) {
            console.error("Error fetching towns:", err);
            setTownsList([]);
        } finally {
            setLoadingTowns(false);
        }
    };

    const handleOpenUnplannedModal = () => {
        setUnplannedModalOpen(true);
        setNcaRemark("");
        fetchAgendasList();
        fetchTownsList();
    };

    const handleSubmitUnplannedNca = async () => {
        if (!selectedAgendaId) {
            toast.warning("Please select an NCA Agenda type.");
            return;
        }
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;

        setSubmittingUnplannedNca(true);
        try {
            const agendaObj = agendaTypeList.find(a => String(a.Agendaid || a.id) === String(selectedAgendaId));
            const townObj = townsList.find(t => {
                const tId = typeof t === 'object' && t !== null
                    ? (t.Itownid ?? t.itownid ?? t.town_id ?? t.TownId ?? t.id ?? t.Stownname ?? t.town_name ?? t.name)
                    : t;
                return String(tId) === String(selectedTownId);
            });
            const posId = localStorage.getItem("positionId") || localStorage.getItem("position_id");
            const empId = localStorage.getItem("employeeId") || localStorage.getItem("employee_id");
            const compId = localStorage.getItem("companyId") || localStorage.getItem("company_id");

            const selectedTownVal = townObj
                ? (townObj.Itownid ?? townObj.itownid ?? townObj.town_id ?? townObj.TownId ?? townObj.id ?? selectedTownId)
                : selectedTownId;
            const selectedTownName = townObj
                ? (townObj.Stownname || townObj.TownName || townObj.town_name || townObj.name || townObj.Town || townObj.label || "")
                : "";

            const url = `${serverUrl}${API.insertDayPlan}`;
            const payload = {
                DcrDate: moment(selectedDate).format("YYYY-MM-DD"),
                tp_date: moment(selectedDate).format("YYYY-MM-DD"),
                DayPlanId: null,
                PositionId: Number(posId || 0),
                position_id: Number(posId || 0),
                CompanyId: Number(compId || 1),
                EmployeeId: Number(empId || 0),
                Agendacontroltype: agendaObj?.Agendacontroltype || "NCA",
                agendacontroltype: agendaObj?.Agendacontroltype || "NCA",
                AgendaId: Number(agendaObj?.Agendaid || selectedAgendaId),
                agenda_id: Number(agendaObj?.Agendaid || selectedAgendaId),
                Agendname: agendaObj?.Agendname || "",
                Itownid: Number(selectedTownVal || 0),
                itownid: Number(selectedTownVal || 0),
                Stownname: selectedTownName,
                OutletOrgDataId: null,
                outlet_org_data_id: null,
                Isjw: false,
                isjw: false,
                DcrStatus: "pending",
                status: "pending",
                is_planned: false,
                reason: "ADDED_THROUGH_CUSTOMER",
                mtp_day_id: 0,
                DeviceTime: moment().format("YYYY-MM-DD HH:mm:ss"),
                DeviceMake: "Web Admin",
                Isprocessed: false,
                remark: ncaRemark || null
            };

            const res = await postData(url, true, payload);

            if (res.status === 200 || res.data?.statusCode === 200) {
                toast.success(res.data?.message || "Unplanned NCA inserted successfully!");
                setUnplannedModalOpen(false);
                fetchNcaData(selectedDate);
                if (onRefreshCounts) onRefreshCounts();
            } else {
                toast.error(res.data?.message || res.data?.error || "Failed to insert unplanned NCA");
            }
        } catch (err: any) {
            console.error("Error submitting insertDayPlan for NCA:", err);
            toast.error(err?.message || "An error occurred while submitting unplanned NCA");
        } finally {
            setSubmittingUnplannedNca(false);
        }
    };

    const fetchNcaData = async (targetDate: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingNcaData(true);
        try {
            const formattedDateParam = moment(targetDate).format('YYYY-MM-DD');
            const url = `${serverUrl}${API.getNca}`;
            const response = await getData(url, true, { date: formattedDateParam });
            console.log("NCA API response:", response);
            if (response.status === 200 || response.data?.statusCode === 200) {
                let list: any[] = [];
                const d = response.data;
                const payload = d?.data || d;

                const dailycallsRaw = Array.isArray(payload?.dailycalls) ? payload.dailycalls : [];
                const dayplanRaw = Array.isArray(payload?.dayplan) ? payload.dayplan : [];

                const dailycallsFormatted = dailycallsRaw.map((item: any) => ({
                    ...item,
                    isDailyCall: true,
                    dcr_status: item.dcr_status ?? item.DcrStatus ?? 'Reported',
                    DcrStatus: item.DcrStatus ?? item.dcr_status ?? 'Reported'
                }));

                const dayplanFormatted = dayplanRaw
                    .filter((item: any) => `${item.DcrStatus ?? item.dcr_status ?? item.status ?? ''}`.toLowerCase() !== 'completed')
                    .map((item: any) => ({
                        ...item,
                        isDailyCall: false
                    }));

                if (payload?.dayplan !== undefined || payload?.dailycalls !== undefined) {
                    list = [...dayplanFormatted, ...dailycallsFormatted];
                } else if (Array.isArray(payload)) {
                    list = payload;
                } else if (Array.isArray(payload?.items)) {
                    list = payload.items;
                } else if (Array.isArray(payload?.data)) {
                    list = payload.data;
                } else if (payload && typeof payload === 'object' && !Array.isArray(payload) && (payload.DcrId || payload.dcr_id || payload.DayPlanId || payload.day_plan_id || payload.AgendaId || payload.agenda_id)) {
                    list = [payload];
                } else {
                    list = [];
                }

                const formattedList = list.map(item => {
                    const rawStatus = item.isDailyCall ? 'Reported' : (item.dcr_status ?? item.DcrStatus ?? item.status ?? 'pending');

                    let commentsVal = item.NcaComments ?? item.nca_comments ?? item.remark ?? item.comments ?? '';
                    let parsedReason = "Half Day First half";
                    let parsedRemark = "";

                    let commentsObj: any = null;
                    if (typeof commentsVal === 'object' && commentsVal !== null) {
                        commentsObj = commentsVal;
                    } else if (typeof commentsVal === 'string' && commentsVal.trim().startsWith('{')) {
                        try {
                            commentsObj = JSON.parse(commentsVal);
                        } catch (e) {
                            parsedRemark = commentsVal;
                        }
                    } else if (typeof commentsVal === 'string') {
                        parsedRemark = commentsVal;
                    }

                    if (commentsObj) {
                        if (commentsObj.reason) {
                            parsedReason = commentsObj.reason;
                            parsedRemark = commentsObj.comment || commentsObj.remark || "";
                        } else if (commentsObj.remark !== undefined && commentsObj.comment !== undefined) {
                            parsedReason = commentsObj.remark;
                            parsedRemark = commentsObj.comment;
                        } else if (commentsObj.remark) {
                            const knownReasons = ["Half Day First half", "Half Day Second half", "Full Day"];
                            if (knownReasons.includes(commentsObj.remark)) {
                                parsedReason = commentsObj.remark;
                                parsedRemark = commentsObj.comment || "";
                            } else {
                                parsedRemark = commentsObj.remark;
                            }
                        } else if (commentsObj.comment) {
                            parsedRemark = commentsObj.comment;
                        }
                    }

                    return {
                        ...item,
                        dcr_id: item.dcr_id ?? item.id ?? item.DcrId ?? item.dcrId,
                        DcrId: item.DcrId ?? item.dcr_id ?? item.id,
                        dcr_status: rawStatus,
                        DcrStatus: rawStatus,
                        nca_reason: parsedReason,
                        nca_remark: parsedRemark,
                        nca_comments: commentsVal,
                        NcaComments: commentsVal,
                        nca_attendees: Array.isArray(item.nca_attendees) ? item.nca_attendees : [],
                        employee_feedback: Array.isArray(item.employee_feedback) ? item.employee_feedback : [],
                        managers: item.managers ?? item.manager_id ?? ''
                    };
                });

                setNcaList(formattedList);
            } else {
                setNcaList([]);
            }
        } catch (error) {
            console.error("Error fetching NCA data:", error);
            setNcaList([]);
        } finally {
            setLoadingNcaData(false);
        }
    };

    useEffect(() => {
        const activeDate = location.state?.date || reduxSelectedDate || localStorage.getItem("selectedDate") || moment().format('YYYY-MM-DD');
        if (activeDate !== selectedDate) {
            setSelectedDate(activeDate);
        }
    }, [reduxSelectedDate, location.state?.date]);

    useEffect(() => {
        if (selectedDate) {
            if (selectedDate !== reduxSelectedDate) {
                dispatch(setReduxSelectedDate(selectedDate));
            }
            fetchNcaData(selectedDate);
            fetchAgendasList();
            fetchTownsList();
        }
    }, [selectedDate]);

    const [submittingNcaIndex, setSubmittingNcaIndex] = useState<number | null>(null);

    const handleRemoveNca = (indexToRemove: number) => {
        if (window.confirm("Are you sure you want to remove this NCA record?")) {
            setNcaList(prev => prev.filter((_, i) => i !== indexToRemove));
            toast.success("NCA record removed successfully");
        }
    };

    const handleUpdateNca = async (targetIdx?: number) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        if (!ncaList || ncaList.length === 0) {
            toast.error("No NCA records to update");
            return;
        }

        const targetList = typeof targetIdx === 'number' ? [ncaList[targetIdx]] : ncaList;

        if (typeof targetIdx === 'number') {
            setSubmittingNcaIndex(targetIdx);
        } else {
            setSubmittingNcaData(true);
        }

        try {
            const url = `${serverUrl}${API.updateNca}`;
            const posId = localStorage.getItem("positionId") || localStorage.getItem("position_id");
            const empId = localStorage.getItem("employeeId") || localStorage.getItem("employee_id");
            const compId = localStorage.getItem("companyId") || localStorage.getItem("company_id");

            const itemsPayload = targetList.map((item: any) => {
                const ncaCommentsObj = {
                    remark: item.nca_reason || "Half Day First half",
                    comment: item.nca_remark || ""
                };

                const rawDayPlanId = item.day_plan_id ?? item.DayPlanId ?? item.day_plan_id ?? item.dcr_id ?? item.DcrId ?? item.id;
                const dayPlanIdNum = (rawDayPlanId !== undefined && rawDayPlanId !== null && !isNaN(Number(rawDayPlanId)))
                    ? Number(rawDayPlanId)
                    : 0;

                const payloadItem: any = {
                    dayplan_id: dayPlanIdNum,
                    dcr_status: "Reported",
                    nca_comments: JSON.stringify(ncaCommentsObj),
                    nca_attendees: item.nca_attendees || [],
                    managers: typeof item.managers === 'string' ? item.managers : String(item.managers || "")
                };



                return payloadItem;
            });

            const payload = itemsPayload.length === 1 ? itemsPayload[0] : { items: itemsPayload };
            const response = await postData(url, true, payload);

            if (response.status === 200 || response.data?.statusCode === 200 || response.data?.status === 200 || response.data?.success) {
                toast.success(response.data?.message || "NCA record updated successfully!");
                if (typeof targetIdx === 'number') {
                    setExpandedCards(prev => ({ ...prev, [targetIdx]: false }));
                } else {
                    setExpandedCards({});
                }
                await fetchNcaData(selectedDate);
                if (onRefreshCounts) onRefreshCounts();
            } else {
                toast.error(response.data?.message || response.data?.error || "Failed to update NCA");
            }
        } catch (error: any) {
            console.error("NCA update error:", error);
            toast.error(error?.message || "An error occurred while updating NCA");
        } finally {
            setSubmittingNcaData(false);
            setSubmittingNcaIndex(null);
        }
    };

    const getStatusBadgeColor = (status: string) => {
        switch (`${status || ''}`.toLowerCase()) {
            case 'completed':
            case 'reported':
                return 'success';
            case 'awatingfeedback':
            case 'pending':
            case 'in_progress':
                return 'warning';
            case 'rejected':
            case 'cancelled':
                return 'danger';
            default:
                return 'primary';
        }
    };

    return (
        <Fragment>
            <Container fluid className="p-4 bg-light min-vh-100">
                <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
                    <div className="card-header bg-white border-bottom p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <CardHeaderSpan
                            headingClassName="pb-0 fw-bold text-dark"
                            back={false}
                            heading={`Non-Call Activity (${getLabel("NCA")})`}
                            span={`Non-Call Activity Records for ${moment(selectedDate).format('dddd, DD MMMM YYYY')}`}
                        />
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                color="success"
                                className="d-flex align-items-center gap-1 px-3 py-2 shadow-sm text-white border-0"
                                style={{ backgroundColor: '#28a745', borderRadius: '6px', fontSize: '0.85rem' }}
                                onClick={handleOpenUnplannedModal}
                            >
                                <FeatherIconCom iconName="Plus" size={15} />
                                <span>Unplanned {getLabel("NCA")}</span>
                            </Button>
                            <Button
                                color="dark"
                                outline
                                className="d-flex align-items-center gap-1 px-3 py-2 fw-semibold btn-back-to-me"
                                style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                                onClick={() => {
                                    dispatch(setReduxSelectedDate(selectedDate));
                                    navigate('/me', { state: { date: selectedDate } });
                                }}
                            >
                                <FeatherIconCom iconName="ArrowLeft" size={15} />
                                <span>Back to ME</span>
                            </Button>
                        </div>
                    </div>

                    {/* Agenda Tabs Navigation (Excluding Mtp_Deviation per instructions) */}
                    <div className="d-flex align-items-center gap-4 mt-3 border-bottom pb-2 flex-wrap px-3" style={{ fontSize: '0.95rem' }}>
                        <span
                            className="fw-bold text-muted cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (onTabChange) onTabChange('JW');
                                else navigate('/me/JW', { state: { date: selectedDate } });
                            }}
                        >
                            Joint Working {agendaCounts ? `- ${agendaCounts.jw}` : ''}
                        </span>
                        <span
                            className="fw-bold text-muted cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (onTabChange) onTabChange('Doctor');
                                else navigate('/me/FW', { state: { date: selectedDate, filter: 'Doctor' } });
                            }}
                        >
                            Doctor {agendaCounts ? `- ${agendaCounts.doctorReported} / ${agendaCounts.doctorTotal}` : ''}
                        </span>
                        <span
                            className="fw-bold text-muted cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (onTabChange) onTabChange('Pharmacy');
                                else navigate('/me/FW', { state: { date: selectedDate, filter: 'Pharmacy' } });
                            }}
                        >
                            Pharmacy {agendaCounts ? `- ${agendaCounts.pharmacyReported} / ${agendaCounts.pharmacyTotal}` : ''}
                        </span>
                        <span
                            className="fw-bold text-muted cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (onTabChange) onTabChange('Stockist');
                                else navigate('/me/FW', { state: { date: selectedDate, filter: 'Stockist' } });
                            }}
                        >
                            Stockist {agendaCounts ? `- ${agendaCounts.stockistReported} / ${agendaCounts.stockistTotal}` : ''}
                        </span>
                        <span
                            className="fw-bold text-primary border-bottom border-primary border-3 pb-2 mb-n2 cursor-pointer"
                            style={{ cursor: 'pointer' }}
                        >
                            {getLabel("NCA")} - {agendaCounts ? agendaCounts.nca : ncaList.length}
                        </span>
                    </div>

                    <CardBody className="p-4" style={{ color: '#000000', backgroundColor: '#f8fafc' }}>
                        {loadingNcaData ? (
                            <Loader height="250px" />
                        ) : ncaList.length > 0 ? (
                            <Row className="g-3">
                                {ncaList.map((item: any, idx: number) => {
                                    const dcrId = item.dcr_id ?? (idx + 1);
                                    const empName = item.employee_details?.name || item.mr_name || 'N/A';
                                    const empDesignation = item.employee_details?.designation || item.mr_details?.designation || '';

                                    const matchingAgenda = agendaTypeList.find(a =>
                                        String(a.Agendaid || a.id || a.agenda_id) === String(item.AgendaId || item.agenda_id || item.agendaId)
                                    );
                                    const dynamicNcaName = (
                                        item.agendname ||
                                        item.Agendname ||
                                        item.agenda_name ||
                                        item.AgendaName ||
                                        matchingAgenda?.Agendname ||
                                        matchingAgenda?.agenda_name ||
                                        matchingAgenda?.name ||
                                        item.agenda?.agendname ||
                                        item.agenda?.Agendname ||
                                        item.agenda?.name ||
                                        item.nca_name ||
                                        item.NcaName ||
                                        item.activity_name ||
                                        item.ActivityName ||
                                        item.nca_type ||
                                        item.NcaType ||
                                        item.agenda_type ||
                                        item.AgendaType ||
                                        item.nca_title ||
                                        item.NcaTitle ||
                                        item.outlet?.outlet_name ||
                                        item.outlet_name ||
                                        item.OutletName ||
                                        item.name ||
                                        item.title ||
                                        (agendaTypeList.length > 0 ? agendaTypeList[0].Agendname : 'Camp')
                                    );
                                    const activityTitle = dynamicNcaName;
                                    const agendaType = item.agendacontroltype || 'NCA';
                                    const townName = item.Stownname || item.town_name || item.town || item.outlet?.town_name || 'Ledo';
                                    const dcrStatus = item.dcr_status || 'Reported';
                                    const isReported = `${dcrStatus}`.toLowerCase() === 'reported';
                                    const ncaComments = item.nca_comments || '';

                                    const isExpanded = !!expandedCards[idx];

                                    return (
                                        <Col xs={12} md={6} xl={6} key={idx}>
                                            <Card
                                                className="border shadow-sm h-100 mb-0"
                                                style={{
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    borderColor: '#d4e3fc',
                                                    backgroundColor: '#eef4ff',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                {/* Card Body matching 2nd image */}
                                                <div className="p-3">
                                                    <div
                                                        className="fw-bold text-dark mb-2 text-truncate"
                                                        style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}
                                                        title={activityTitle}
                                                    >
                                                        {activityTitle}
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="d-flex align-items-center gap-2 text-dark small fw-semibold">
                                                            <span
                                                                className="d-inline-flex align-items-center justify-content-center"
                                                                style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#e2e8f0', marginRight: '4px' }}
                                                            >
                                                                <FeatherIconCom iconName="User" size={12} className="text-secondary" />
                                                            </span>
                                                            <span>{getLabel("NCA")}</span>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2 small fw-semibold text-dark">
                                                            <FeatherIconCom
                                                                iconName="Phone"
                                                                size={14}
                                                                style={{ color: '#0d6efd', stroke: '#0d6efd', fill: '#0d6efd', marginRight: '1px' }}
                                                            />
                                                            <span>{townName}</span>
                                                        </div>
                                                    </div>

                                                    {isReported && (item.nca_reason || item.nca_remark) && (
                                                        <div className="mt-2 pt-2 border-top small" style={{ fontSize: '0.82rem' }}>
                                                            {item.nca_reason && (
                                                                <div className="text-dark fw-semibold mb-1">
                                                                    <span className="text-muted me-1">Reason:</span>
                                                                    <Badge color="primary" pill className="fw-bold px-2 py-1">{item.nca_reason}</Badge>
                                                                </div>
                                                            )}
                                                            {item.nca_remark && (
                                                                <div className="text-dark text-truncate" title={item.nca_remark}>
                                                                    <span className="text-muted me-1">Remark:</span>
                                                                    <span className="fw-semibold text-dark">{item.nca_remark}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Action Footer matching 2nd image */}
                                                <div
                                                    className="d-flex align-items-center justify-content-between px-3 py-2"
                                                    style={{ backgroundColor: '#dbeafe', borderTop: '1px solid #bfdbfe' }}
                                                >
                                                    {!isReported ? (
                                                        <>
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                className="d-flex align-items-center gap-1 px-3 py-1.5 fw-semibold border-0 text-white shadow-sm"
                                                                style={{ backgroundColor: '#dc3545', borderRadius: '6px', fontSize: '0.82rem' }}
                                                                onClick={() => handleRemoveNca(idx)}
                                                            >
                                                                <FeatherIconCom iconName="Trash2" size={13} />
                                                                <span>Remove {getLabel("NCA")}</span>
                                                            </Button>

                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                className="d-flex align-items-center gap-1 px-3 py-1.5 fw-semibold border-0 text-white shadow-sm"
                                                                style={{ backgroundColor: '#0d6efd', borderRadius: '6px', fontSize: '0.82rem' }}
                                                                onClick={() => toggleCard(idx)}
                                                            >
                                                                <span>Report {getLabel("NCA")}</span>
                                                                <FeatherIconCom iconName="ExternalLink" size={13} />
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <div className="d-flex align-items-center justify-content-between w-100">
                                                            <span
                                                                className="badge px-3 py-2 fw-bold text-white d-flex align-items-center gap-1 shadow-sm"
                                                                style={{ backgroundColor: '#48bb78', borderRadius: '6px', fontSize: '0.82rem' }}
                                                            >
                                                                <FeatherIconCom iconName="CheckCircle" size={14} />
                                                                <span>{getLabel("NCA")} Reported</span>
                                                            </span>
                                                            <Button
                                                                color="link"
                                                                size="sm"
                                                                className="p-0 text-primary fw-semibold shadow-none border-0 text-decoration-none"
                                                                style={{ fontSize: '0.82rem' }}
                                                                onClick={() => toggleCard(idx)}
                                                            >
                                                                View Details
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Direct Modal Popup for NCA reporting / view */}
                                                <Modal isOpen={isExpanded} toggle={() => toggleCard(idx)} size="md" centered scrollable>
                                                    <ModalHeader toggle={() => toggleCard(idx)} className="bg-light border-bottom">
                                                        <div>
                                                            <h5 className="fw-bold text-dark mb-0">{activityTitle}</h5>
                                                            <small className="text-muted">{getLabel("NCA")} • {townName}</small>
                                                        </div>
                                                    </ModalHeader>
                                                    <ModalBody className="p-4 bg-white" style={{ color: '#000000' }}>
                                                        {/* Reason Selection */}
                                                        <div className="mb-3">
                                                            <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Reason</label>
                                                            <Input
                                                                type="select"
                                                                value={item.nca_reason || "Half Day First half"}
                                                                disabled={isReported}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    setNcaList(prev => prev.map((it, i) => i === idx ? { ...it, nca_reason: val } : it));
                                                                }}
                                                                className="bg-white border text-dark shadow-none py-2 px-3 fw-semibold"
                                                                style={{ fontSize: '0.9rem', color: '#000000' }}
                                                            >
                                                                <option value="Half Day First half">Half Day First half</option>
                                                                <option value="Half Day Second half">Half Day Second half</option>
                                                                <option value="Full Day">Full Day</option>
                                                            </Input>
                                                        </div>

                                                        {/* Remark / Comment */}
                                                        <div className="mb-3">
                                                            <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Remark / Comments</label>
                                                            <Input
                                                                type="textarea"
                                                                rows={3}
                                                                placeholder="Enter remark..."
                                                                value={item.nca_remark || ""}
                                                                disabled={isReported}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    setNcaList(prev => prev.map((it, i) => i === idx ? { ...it, nca_remark: val } : it));
                                                                }}
                                                                className="bg-white border text-dark shadow-none py-2 px-3 fw-semibold"
                                                                style={{ fontSize: '0.9rem', color: '#000000' }}
                                                            />
                                                        </div>

                                                        {/* Per-card Submit Button */}
                                                        {!isReported && (
                                                            <div className="d-flex justify-content-end mt-3 pt-2 border-top">
                                                                <Button
                                                                    color="success"
                                                                    className="px-4 py-2 fw-bold border-0 text-white d-flex align-items-center gap-2"
                                                                    disabled={submittingNcaIndex === idx || submittingNcaData}
                                                                    style={{ backgroundColor: '#198754', fontSize: '0.9rem', borderRadius: '8px', cursor: 'pointer' }}
                                                                    onClick={() => handleUpdateNca(idx)}
                                                                >
                                                                    {submittingNcaIndex === idx ? <Spinner size="sm" color="light" /> : (
                                                                        <>
                                                                            <FeatherIconCom iconName="CheckCircle" size={16} />
                                                                            <span>Submit {getLabel("NCA")} Record</span>
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </ModalBody>
                                                </Modal>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        ) : (
                            <div className="text-center py-5" style={{ color: '#000000' }}>
                                <FeatherIconCom iconName="Calendar" size={48} className="mb-3 text-dark" />
                                <h5 className="fw-bold text-dark" style={{ color: '#000000' }}>No Non-Call Activity Records Found</h5>
                                <p className="small mb-0 text-dark fw-semibold" style={{ color: '#000000' }}>
                                    No non-call activity data is available for {moment(selectedDate).format('DD MMM YYYY')}.
                                </p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>

            {/* Unplanned NCA Modal */}
            <Modal isOpen={unplannedModalOpen} toggle={() => setUnplannedModalOpen(!unplannedModalOpen)} size="md" centered>
                <ModalHeader toggle={() => setUnplannedModalOpen(!unplannedModalOpen)} className="bg-light border-bottom">
                    <FeatherIconCom iconName="PlusCircle" size={18} className="me-2 text-primary" />
                    <span className="fw-bold text-dark">Unplanned {getLabel("NCA")} +</span>
                </ModalHeader>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    <FormGroup className="mb-3">
                        <Label className="form-label small fw-bold text-dark mb-1">Select Agenda Type ({getLabel("NCA")})</Label>
                        {loadingAgendas ? (
                            <div className="py-2 text-muted small">
                                <Spinner size="sm" color="primary" className="me-2" /> Loading Agendas...
                            </div>
                        ) : (
                            <Input
                                type="select"
                                value={selectedAgendaId}
                                onChange={(e) => setSelectedAgendaId(e.target.value)}
                                className="bg-white border text-dark shadow-none fw-semibold py-2"
                                style={{ fontSize: '0.88rem' }}
                            >
                                {agendaTypeList.length > 0 ? (
                                    agendaTypeList.map((a: any) => {
                                        const aId = a.Agendaid || a.id;
                                        const aName = a.Agendname || a.name || `Agenda #${aId}`;
                                        return (
                                            <option key={aId} value={String(aId)}>
                                                {aName}
                                            </option>
                                        );
                                    })
                                ) : (
                                    <option value="">No Agenda types available</option>
                                )}
                            </Input>
                        )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                        <Label className="form-label small fw-bold text-dark mb-1">Select Town / Patch</Label>
                        {loadingTowns ? (
                            <div className="py-2 text-muted small">
                                <Spinner size="sm" color="primary" className="me-2" /> Loading Towns...
                            </div>
                        ) : (
                            <Input
                                type="select"
                                value={selectedTownId}
                                onChange={(e) => setSelectedTownId(e.target.value)}
                                className="bg-white border text-dark shadow-none fw-semibold py-2"
                                style={{ fontSize: '0.88rem' }}
                            >
                                {townsList.length > 0 ? (
                                    townsList.map((t: any, idx: number) => {
                                        const tId = typeof t === 'object' && t !== null
                                            ? (t.Itownid ?? t.itownid ?? t.town_id ?? t.TownId ?? t.id ?? t.Stownname ?? t.town_name ?? t.name ?? idx)
                                            : t;
                                        const tName = typeof t === 'object' && t !== null
                                            ? (t.Stownname || t.TownName || t.town_name || t.name || t.Town || t.label || `Town #${tId}`)
                                            : String(t);
                                        return (
                                            <option key={tId || idx} value={String(tId)}>
                                                {tName}
                                            </option>
                                        );
                                    })
                                ) : (
                                    <option value="">No Towns available</option>
                                )}
                            </Input>
                        )}
                    </FormGroup>


                </ModalBody>
                <ModalFooter className="bg-light d-flex justify-content-end gap-2">
                    <Button color="secondary" size="sm" outline onClick={() => setUnplannedModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        size="sm"
                        className="px-4 fw-bold text-white"
                        disabled={!selectedAgendaId || submittingUnplannedNca}
                        onClick={handleSubmitUnplannedNca}
                    >
                        {submittingUnplannedNca ? (
                            <>
                                <Spinner size="sm" color="light" className="me-1" /> Submitting...
                            </>
                        ) : (
                            <>
                                <FeatherIconCom iconName="Check" size={14} className="me-1" /> Submit Unplanned {getLabel("NCA")}
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
}
